// iter-35g — Bump config fetcher with viewport detection + window-resize
// re-evaluation. Used by SideBumps / DiscoverBumps to render whichever
// viewport's config matches the current screen. Single in-memory cache so
// every silo page makes at most one network call.
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

let cached = null;
let inflight = null;

export async function fetchBumpConfig() {
  if (cached) return cached;
  if (!inflight) {
    inflight = api.get("/bump-config").then((r) => {
      cached = r.data;
      inflight = null;
      return cached;
    }).catch(() => {
      inflight = null;
      return null;
    });
  }
  return inflight;
}

export function invalidateBumpConfig() {
  cached = null;
}

export function useBumpConfig() {
  const [cfg, setCfg] = useState(cached);
  const [viewport, setViewport] = useState(() =>
    typeof window !== "undefined" && window.innerWidth >= 640 ? "desktop" : "mobile"
  );
  useEffect(() => {
    let alive = true;
    fetchBumpConfig().then((c) => { if (alive && c) setCfg(c); });
    const onResize = () => {
      const next = window.innerWidth >= 640 ? "desktop" : "mobile";
      setViewport((p) => (p === next ? p : next));
    };
    window.addEventListener("resize", onResize);
    return () => { alive = false; window.removeEventListener("resize", onResize); };
  }, []);
  return { cfg, viewport };
}

// Convert a slot index 1..N into a CSS top-percentage. Distributes slots
// evenly across the viewport. Feb 14, 2026 PM — padding reduced from 12%
// to 3% per user request: bumps can now anchor at the very top and the
// very bottom of the viewport. Visual overlap is the creator's call —
// the cap was a safety rail that limited expressiveness.
export function slotToTopPct(slot, maxSlots) {
  if (!slot || !maxSlots) return "30%";
  const padding = 3;             // top + bottom dead-zone in %
  const usable = 100 - padding * 2;
  const denom = Math.max(1, maxSlots - 1);
  const pct = padding + ((slot - 1) / denom) * usable;
  return `${pct.toFixed(2)}%`;
}

// Map semantic color slot → CSS variable. The actual hex is provided by
// whichever surface the bump renders on (PublicSilo template palette,
// Dashboard default palette, etc.) via `tpl-*` variables.
// Feb 18, 2026 — All slots now wire to template CSS vars so side tabs
// honor the silo's dark/light/cream theme regardless of which slot the
// creator picks. Previously `muted` and `inverse` were hardcoded grays
// that stood out on dark templates.
export const COLOR_SLOT_VAR = {
  primary: { bg: "var(--tpl-text)",        fg: "var(--tpl-surface)" },
  accent:  { bg: "var(--tpl-accent-color, #4A5D4E)", fg: "var(--tpl-surface, #FDFCF8)" },
  surface: { bg: "var(--tpl-surface)",     fg: "var(--tpl-text)" },
  muted:   { bg: "var(--tpl-muted-bg, var(--tpl-surface))", fg: "var(--tpl-muted, #6B6A68)" },
  inverse: { bg: "var(--tpl-text)",        fg: "var(--tpl-surface)" },
};

// Map shape id → Tailwind class string applied to the tab button.
// iter-39 Sprint 3: expanded from 5 → 13 shapes. Each shape has a left-edge and
// right-edge variant — when adding a new shape, ALWAYS add both maps below.
export const SHAPE_CLASS = {
  "tab-classic":     "rounded-r-md border-l-0 shadow-md",                                 // for left side
  "tab-pill":        "rounded-r-3xl border-l-0 shadow-md",
  "tab-slab":        "rounded-none border-l-0 shadow-md",
  "tab-notch":       "rounded-r-md border-l-0 shadow-md [clip-path:polygon(0_0,100%_0,100%_calc(100%-12px),calc(100%-12px)_100%,0_100%)]",
  "tab-floating":    "rounded-md ml-1 shadow-2xl border",
  "tab-trapezoid":   "rounded-r-sm border-l-0 shadow-md [clip-path:polygon(0_0,100%_15%,100%_85%,0_100%)]",
  "tab-arrow":       "border-l-0 shadow-md [clip-path:polygon(0_0,calc(100%-14px)_0,100%_50%,calc(100%-14px)_100%,0_100%)]",
  "tab-blade":       "rounded-r-2xl border-l-0 shadow-md [clip-path:polygon(0_0,calc(100%-6px)_8px,100%_50%,calc(100%-6px)_calc(100%-8px),0_100%)]",
  "tab-flag":        "rounded-r-md border-l-0 shadow-md [clip-path:polygon(0_0,100%_0,calc(100%-10px)_50%,100%_100%,0_100%)]",
  "tab-stepped":     "rounded-r-sm border-l-0 shadow-md [clip-path:polygon(0_0,calc(100%-8px)_0,calc(100%-8px)_30%,100%_30%,100%_70%,calc(100%-8px)_70%,calc(100%-8px)_100%,0_100%)]",
  "tab-square-cut":  "rounded-none border-l-0 shadow-md [clip-path:polygon(0_0,100%_0,calc(100%-10px)_50%,100%_100%,0_100%)]",
  "tab-rounded-out": "rounded-r-[24px] border-l-0 shadow-md",
  "tab-wave":        "rounded-r-2xl border-l-0 shadow-md [clip-path:polygon(0_0,90%_0,100%_30%,90%_50%,100%_70%,90%_100%,0_100%)]",
};

// Mirror for the right edge — flips the rounding direction.
export const SHAPE_CLASS_RIGHT = {
  "tab-classic":     "rounded-l-md border-r-0 shadow-md",
  "tab-pill":        "rounded-l-3xl border-r-0 shadow-md",
  "tab-slab":        "rounded-none border-r-0 shadow-md",
  "tab-notch":       "rounded-l-md border-r-0 shadow-md [clip-path:polygon(0_0,100%_0,100%_100%,12px_100%,0_calc(100%-12px))]",
  "tab-floating":    "rounded-md mr-1 shadow-2xl border",
  "tab-trapezoid":   "rounded-l-sm border-r-0 shadow-md [clip-path:polygon(0_15%,100%_0,100%_100%,0_85%)]",
  "tab-arrow":       "border-r-0 shadow-md [clip-path:polygon(14px_0,100%_0,100%_100%,14px_100%,0_50%)]",
  "tab-blade":       "rounded-l-2xl border-r-0 shadow-md [clip-path:polygon(6px_8px,100%_0,100%_100%,6px_calc(100%-8px),0_50%)]",
  "tab-flag":        "rounded-l-md border-r-0 shadow-md [clip-path:polygon(10px_50%,0_0,100%_0,100%_100%,0_100%)]",
  "tab-stepped":     "rounded-l-sm border-r-0 shadow-md [clip-path:polygon(8px_0,100%_0,100%_100%,8px_100%,8px_70%,0_70%,0_30%,8px_30%)]",
  "tab-square-cut":  "rounded-none border-r-0 shadow-md [clip-path:polygon(10px_50%,0_0,100%_0,100%_100%,0_100%)]",
  "tab-rounded-out": "rounded-l-[24px] border-r-0 shadow-md",
  "tab-wave":        "rounded-l-2xl border-r-0 shadow-md [clip-path:polygon(10%_0,100%_0,100%_100%,10%_100%,0_70%,10%_50%,0_30%)]",
};
